import React, { useState, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import { SessionContext } from "../context/sessionContext";
import SurveyFlowHeader from "../components/SurveyFlowHeader";
import { savePendingGenerateDraft, markStepReached, getMaxStepReached } from "../utils/surveyFlowStorage";

const sizeOptions = [
  {
    id: "focused",
    label: "Focused",
    sub: "10–15 questions",
    icon: "/json-media/icons/target-arrow.svg",
  },
  {
    id: "moderate",
    label: "Moderate",
    sub: "15–30 questions",
    icon: "/json-media/icons/cup-hot.svg",
  },
  {
    id: "comprehensive",
    label: "Comprehensive",
    sub: "30–40 questions",
    icon: "/json-media/icons/diamond.svg",
  },
];

const STORAGE_KEY = "surveySpecifications";

export default function SurveyRobustness({ onBack }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, getUserId } = useContext(SessionContext);
  const specs =
    location.state ||
    (() => {
      try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
      } catch {
        return {};
      }
    })();

  const [selected, setSelected] = useState(specs.robustness || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [maxStepReached] = useState(() => {
    markStepReached("robustness");
    return getMaxStepReached() || "robustness";
  });

  const handleSelect = (id) => {
    setSelected(id);
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...saved, robustness: id }));
    } catch (_) {}
  };

  const handleGenerateSurvey = async () => {
    if (!selected) return;

    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ ...saved, robustness: selected }),
      );
    } catch (_) {}

    setLoading(true);
    setError(null);

    const payload = {
      coreQuestions: specs.coreQuestions || "",
      businessContext: specs.businessContext || "",
      targetAudience: specs.targetAudience || "",
      keyConcepts: specs.keyConcepts || "",
      surveyType: specs.selectedType || "",
      robustness: selected,
    };

    try {
      const token = Cookies.get("token");
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/generated/survey`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", token: token },
          body: JSON.stringify(payload),
        },
      );

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || `Server error: ${res.status}`);
      }

      const data = await res.json();

      if (!data.success) {
        throw new Error(data.message || "Survey generation failed.");
      }

      const requestId = data.requestId;

      if (isAuthenticated) {
        savePendingGenerateDraft(getUserId?.(), "generated", { requestId });
      }

      await pollForSurvey(requestId);
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  const pollForSurvey = async (requestId, attempts = 0) => {
    const MAX_ATTEMPTS = 30;
    const INTERVAL_MS = 2000;

    if (attempts >= MAX_ATTEMPTS) {
      setError("Survey generation is taking too long. Please try again later.");
      setLoading(false);
      return;
    }

    try {
      const token = Cookies.get("token");
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/generated/survey/${requestId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            token: token,
          },
        },
      );

      if (!res.ok) {
        throw new Error(`Server error: ${res.status}`);
      }

      const data = await res.json();

      if (data.success && data.survey && data.survey.sections?.length > 0) {
        try {
          const saved = JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
          localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({
              ...saved,
              requestId,
              surveyPayload: { ...data, requestId },
            }),
          );
        } catch (_) {}
        setLoading(false);
        navigate("/generated-survey", {
          state: { surveyData: { ...data, requestId } },
        });
        return;
      }

      setTimeout(() => pollForSurvey(requestId, attempts + 1), INTERVAL_MS);
    } catch (err) {
      setError(err.message || "Failed to fetch survey. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="page-bg">
      
        <SurveyFlowHeader
          flow="generated"
          activeStep="robustness"
          isAuthenticated={isAuthenticated}
          maxStepReached={maxStepReached}
        />
        <div className="content-center">
        <div className="survey-container survey-container--wide">
          <h1 className="survey-page-title">
            How robust you want your survey to be?
          </h1>
          <p className="survey-page-subtitle">
            This includes the number of questions and the level of detail in the
            answers, which directly impacts pricing.
          </p>
          <div className="size-cards">
            {sizeOptions.map(({ id, label, sub, icon }) => (
              <div
                key={id}
                className={`size-card${selected === id ? " size-card--selected" : ""}`}
                onClick={() => !loading && handleSelect(id)}
                role="radio"
                aria-checked={selected === id}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !loading) handleSelect(id);
                }}
              >
                <span className="size-card__icon">
                  <img src={icon} alt={label} />
                </span>
                <span className="size-card__title">{label}</span>
                <span className="size-card__sub">{sub}</span>
              </div>
            ))}
          </div>
          {error && (
            <div
              className="error-banner"
              style={{
                marginTop: "1rem",
                padding: "0.75rem 1rem",
                background: "#fff0f0",
                border: "1px solid #f5c2c2",
                borderRadius: "8px",
                color: "#c0392b",
                fontSize: "0.9rem",
              }}
            >
              {error}
            </div>
          )}
          <div className="action-bar action-bar--center">
            {typeof onBack === "function" && !loading && (
              <button className="btn-secondary" onClick={onBack}>
                Go Back
              </button>
            )}
            <button
              className="btn-primary"
              onClick={handleGenerateSurvey}
              disabled={!selected || loading}
              style={{ minWidth: "160px", position: "relative" }}
            >
              {loading ? (
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    justifyContent: "center",
                  }}
                >
                  <Spinner />
                  Generating…
                </span>
              ) : (
                "Generate Survey"
              )}
            </button>
          </div>
          {loading && (
            <p
              style={{
                textAlign: "center",
                marginTop: "1rem",
                color: "#ffffff",
                fontSize: "0.875rem",
              }}
            >
              Your survey is being generated. This may take up to a minute…
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function Spinner() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      style={{ animation: "spin 0.75s linear infinite" }}
    >
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  );
}